import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { Table, ProgressBar } from 'react-bootstrap';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { get, isEmpty, keyBy, isNull, isUndefined } from 'lodash';
import { isSoloLearningAsset } from '../../utils/UserLearningAssetUtils';
import RichTextDisplay from '../../components/RichTextDisplay';

import TrackPlayerSlideComponent from '../../hocs/TrackPlayerSlideComponent';
import { SECTION_ITEM_TYPES } from '../../Constants';
import LikertScale from './LikertScale';
import WorkspaceSlideHeader from './WorkspaceSlideHeader';

import VerbText from '../../components/VerbText';

class ShowPreviousAnswersSlide extends Component {
    static propTypes = {
        name: PropTypes.string,
        nextSectionName: PropTypes.string,
        content: PropTypes.shape({
            aggregateLikertResults: PropTypes.shape({
                isActive: PropTypes.bool,
                description: PropTypes.string,
                title: PropTypes.string,
                benchmark: PropTypes.number
            }),
            description: PropTypes.string,
            questionsToDisplay: PropTypes.arrayOf(
                PropTypes.shape({
                    questionType: PropTypes.string,
                    questionId: PropTypes.string
                })
            )
        }),
        associatedQuestions: PropTypes.object,
        canMoveToNext: PropTypes.func,
        canMoveToPrevious: PropTypes.func,
        onChange: PropTypes.func,
        currentUser: PropTypes.object,
        currentUserAnswers: PropTypes.object,
        sectionId: PropTypes.string,
        sectionItemId: PropTypes.string,
        hideBookmark: PropTypes.bool,
        learningAssetType: PropTypes.string,
        sendWorkspaceSegementEvent: PropTypes.func,
        currentUserTrack: PropTypes.object,
        isRefSheet: PropTypes.bool
    };

    componentDidMount() {
        const { canMoveToPrevious, canMoveToNext, onChange } = this.props;

        if (canMoveToPrevious) canMoveToPrevious(true);
        if (canMoveToNext) canMoveToNext(true);

        const isCompleted = true;
        const isAnswered = true;
        if (onChange) onChange(null, isCompleted, isAnswered);
    }

    /**
     *
     * @param {Object} sectionItem
     * @param {string} sectionItem.type
     * @param {Object} completedDataLookup
     * @return {*}
     * @private
     */
    _getUserAnswer = (sectionItem, completedDataLookup, isMobile) => {
        const formattedSectionType = (sectionItem.type || '').toLocaleUpperCase();
        const userValue = get(completedDataLookup, `${sectionItem.id}.data`, '');

        let currentUserAnswer = '';
        if (formattedSectionType.toLocaleUpperCase() === SECTION_ITEM_TYPES.LIKERT_CONTENT) {
            currentUserAnswer = (
                <LikertScale
                    start={sectionItem.content.start}
                    length={sectionItem.content.length}
                    userValue={userValue}
                />
            );
        } else if (formattedSectionType === SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT) {
            currentUserAnswer = '';
            const userDataChoice =
                completedDataLookup && completedDataLookup[sectionItem.id] && completedDataLookup[sectionItem.id].data;
            if ((userDataChoice || '').length > 36) {
                let answerStr = userDataChoice;
                const div = document.createElement("div");
                div.innerHTML = userDataChoice;
                const pTags = div.getElementsByTagName("p");   // or getElementsByClassName if you prefer
                if (pTags && pTags.length && pTags[0]?.innerHTML) {
                    let pHTML = pTags[0].innerHTML;
                    answerStr = pHTML;
                }
                currentUserAnswer = isMobile ? `<li class="answer-list-item">${answerStr.slice(40)}</li>` : userDataChoice.slice(40);
            } else {
                get(sectionItem, 'content.options', []).forEach((option) => {
                    if (option.choice === userDataChoice) {
                        let answerStr = option.label;
                        const div = document.createElement("div");
                        div.innerHTML = option.label;
                        const pTags = div.getElementsByTagName("p");   // or getElementsByClassName if you prefer
                        if (pTags && pTags.length && pTags[0]?.innerHTML) {
                            let pHTML = pTags[0].innerHTML;
                            answerStr = pHTML;
                        }
                        currentUserAnswer = isMobile ? `<li class="answer-list-item">${answerStr}</li>` : option.label;
                    }
                });
            }
        } else if (formattedSectionType === SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT) {
            const selectedDataLookup = {};
            const dataArray = get(completedDataLookup, `[${sectionItem.id}].data`, []);
            if (Array.isArray(dataArray)) {
                dataArray.forEach((item) => {
                    if (item.length > 36) {
                        const [choice, answer] = item.split('::::');
                        selectedDataLookup[choice] = answer;
                    } else {
                        selectedDataLookup[item] = 'Other';
                    }
                });
            }
            const results = [];
            get(sectionItem, 'content.options', []).forEach((option) => {
                if (option.choice in selectedDataLookup) {
                    if (option.isOther) {
                        results.push(
                            selectedDataLookup[option.choice]?.length
                                ? selectedDataLookup[option.choice]
                                      .split('\n')
                                      .map((part) => `<div>${part}</div>`)
                                      .join('\n')
                                : 'Other'
                        );
                    } else {
                        results.push(option.label);
                    }
                }
            });

            if (results.length) {
                const resultsToString = results.reduce((acc, result, currentIndex) => {
                    if (isMobile) {
                        let answerStr = result;
                        const div = document.createElement("div");
                        div.innerHTML = result;
                        const pTags = div.getElementsByTagName("p");   // or getElementsByClassName if you prefer
                        if (pTags && pTags.length && pTags[0]?.innerHTML) {
                            let pHTML = pTags[0].innerHTML;
                            answerStr = pHTML;
                        }
                        return `${acc}${currentIndex > 0 ? '' : ''}<li class="answer-list-item">${answerStr}</li>`;
                    }
                    return `${acc}${currentIndex > 0 ? '\n' : ''}<div>${result}</div>`;
                }, '');
                currentUserAnswer = `<div>${resultsToString}</div>`;
            }
        } else if (formattedSectionType === SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT) {
            const rawAnswer = get(completedDataLookup, `[${sectionItem.id}].data.answer`, '');
            if (rawAnswer.length) {
                const answerToString = rawAnswer.split('\n').map((part) => {
                    return isMobile ? `<div>${part}</div>` : <div>{part}</div>;
                });
                currentUserAnswer = isMobile ? `<div class="form-input-answer">${answerToString}</div>` : answerToString;
            }
        } else if (formattedSectionType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE) {
            currentUserAnswer = '';
            const userDataChoice =
                completedDataLookup && completedDataLookup[sectionItem.id] && completedDataLookup[sectionItem.id].data;
            if ((userDataChoice || '').length > 36) {
                let answerStr = userDataChoice;
                const div = document.createElement("div");
                div.innerHTML = userDataChoice;
                const pTags = div.getElementsByTagName("p");   // or getElementsByClassName if you prefer
                if (pTags && pTags.length && pTags[0]?.innerHTML) {
                    let pHTML = pTags[0].innerHTML;
                    answerStr = pHTML;
                }
                currentUserAnswer = isMobile ? `<li class="answer-list-item">${answerStr.slice(40)}</li>` : userDataChoice.slice(40);
            } else {
                get(sectionItem, 'choiceContent.options', []).forEach((option) => {
                    if (option.choice === userDataChoice) {
                        let answerStr = option.label;
                        const div = document.createElement("div");
                        div.innerHTML = option.label;
                        const pTags = div.getElementsByTagName("p");   // or getElementsByClassName if you prefer
                        if (pTags && pTags.length && pTags[0]?.innerHTML) {
                            let pHTML = pTags[0].innerHTML;
                            answerStr = pHTML;
                        }
                        currentUserAnswer = isMobile ? `<li class="answer-list-item">${answerStr}</li>` : option.label;
                    }
                });
            }
        }
        return currentUserAnswer;
    };

    /**
     *
     * @return {Array}
     * @private
     */
    _generateAnswers = () => {
        const {
            associatedQuestions = {},
            currentUserAnswers,
            currentUser,
            learningAssetType,
            nextSectionName,
            sendWorkspaceSegementEvent
        } = this.props;
        return Object.values(associatedQuestions).map((item, index) => {
            const userAnswer = this._getUserAnswer(item, currentUserAnswers);
            const isLikert = (item.type || '').toLocaleUpperCase() === SECTION_ITEM_TYPES.LIKERT_CONTENT;

            return (
                <tr key={`${item.questionId}-${index}`}>
                    <td className={'review-prev-activities-label-cell'} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<span>${item.name || ''}</span>`) }} />
                    <td>
                        <div key={'you'} className={cns('showPreviousAnswersSlide-answer', { likert: isLikert })}>
                            {isLikert || typeof userAnswer !== 'string' ? (
                                <div className={'showPreviousAnswersSlide-user-answer'}>{userAnswer}</div>
                            ) : (
                                <RichTextDisplay
                                    className={'showPreviousAnswersSlide-user-answer'}
                                    htmlToDisplay={userAnswer}
                                    currentUser={currentUser}
                                    nextSectionName={nextSectionName}
                                    sendSegementEvent={sendWorkspaceSegementEvent}
                                />
                            )}
                        </div>
                    </td>
                </tr>
            );
        });
    };

    _calculateLikertScore = () => {
        const { content, currentUserAnswers } = this.props;
        let scoreToDisplay = 0;

        const sectionItemIdsToCount = (content.questionsToDisplay || []).map((question) => {
            if (question.questionType === 'LIKERT') {
                return question.questionId;
            }
            return null;
        });

        const sectionItemToCountById = keyBy(sectionItemIdsToCount);

        Object.values(currentUserAnswers || {}).forEach((answer) => {
            if (sectionItemToCountById[answer.id] && answer.data !== null) {
                scoreToDisplay += answer.data;
            }
        });

        return scoreToDisplay;
    };

    _getScoreClassName = () => {
        const classNumber = Math.ceil(parseFloat(moment().format('mm') / 60) * 10);
        return `score-box score-color-${classNumber}`;
    };

    _renderScore = (score) => (
        <div className={cns(this._getScoreClassName())}>
            <h5 className={'score'}>SELF-RATING</h5>
            <p className={'score-number'}>{`${score}`}</p>
        </div>
    );

    _calculateLikertsTotalPossibleScore = () => {
        const { associatedQuestions } = this.props;
        let totalScore = 0;

        Object.values(associatedQuestions).forEach((question) => {
            if (question.type && question.type === 'Likert') {
                totalScore += question.content.length;
            }
        });

        return totalScore;
    };

    _renderAnswerScale(score) {
        const { content } = this.props;
        let progressBarWidth = 500;

        if (window.outerWidth < 800) {
            progressBarWidth = 250;
        } else if (window.outerWidth < 1100) {
            progressBarWidth = 300;
        } else if (window.outerWidth < 1300) {
            progressBarWidth = 400;
        }

        const likertPossibleScore = this._calculateLikertsTotalPossibleScore();
        const benchmarkLeftPixel = (content.aggregateLikertResults.benchmark / likertPossibleScore) * progressBarWidth;
        return (
            <div className={'answer-scale'} style={{ width: progressBarWidth + 10 }}>
                <ProgressBar style={{ width: progressBarWidth }} now={score} max={likertPossibleScore} />
                {content.aggregateLikertResults.benchmark && (
                    <div style={{ left: benchmarkLeftPixel }} className={'benchmark'} />
                )}
            </div>
        );
    }

    _renderLikertsAggregationSection = () => {
        const {
            content: { aggregateLikertResults }
        } = this.props;
        const likertScore = this._calculateLikertScore();

        return (
            <div className={'likert-score-result no-horizontal-margin row'}>
                <div className={'col-sm-4 score-container'}>{this._renderScore(likertScore)}</div>
                <div className={'col-sm-8 description-container row'}>
                    <div className={'score-title col-sm-12'}>
                        <h4>{aggregateLikertResults.title || ''}</h4>
                    </div>
                    <div className={'score-description col-sm-12'}>{aggregateLikertResults.description || ''}</div>
                    <div className={'answer-scale-container col-sm-12 col-12'}>
                        {this._renderAnswerScale(likertScore)}
                    </div>
                    <div className={'score-legend row'}>
                        <div className={'col-12'}>
                            <div className={'ms-auto'}>
                                <div className={'expected-score-legend'} />
                                &nbsp; &nbsp;SELF-RATING &nbsp; &nbsp; &nbsp; &nbsp;
                                {aggregateLikertResults.benchmark && (
                                    <Fragment>
                                        <div className={'user-score-legend'} />
                                        &nbsp; &nbsp;REFERENCE POINT
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    _getAnswersMobileView = () => {
        const {
            associatedQuestions = {},
            currentUserAnswers,
            currentUser,
            learningAssetType,
            nextSectionName,
            sendWorkspaceSegementEvent
        } = this.props;
        return Object.values(associatedQuestions).map((item, index) => {
            const userAnswer = this._getUserAnswer(item, currentUserAnswers, true);
            const isLikert = (item.type || '').toLocaleUpperCase() === SECTION_ITEM_TYPES.LIKERT_CONTENT;

            return (
                <div key={`${item.questionId}-${index}`}>
                    <div className={'review-prev-activities-label-cell mobile'} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<span>${item.name || ''}</span>`) }} />
                    <div>
                        <div key={'you'} className={cns('showPreviousAnswersSlide-answer', { likert: isLikert })}>
                            {isLikert || typeof userAnswer !== 'string' ? (
                                <div className={'showPreviousAnswersSlide-user-answer'}>{userAnswer}</div>
                            ) : (
                                <div className={"form-input-answer"}>
                                    <RichTextDisplay
                                        className={'showPreviousAnswersSlide-user-answer'}
                                        htmlToDisplay={userAnswer}
                                        currentUser={currentUser}
                                        nextSectionName={nextSectionName}
                                        sendSegementEvent={sendWorkspaceSegementEvent}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        });
    };

    render() {
        const {
            currentUser,
            name,
            nextSectionName,
            content,
            content: { description = '' },
            sectionId,
            sectionItemId,
            hideBookmark,
            sendWorkspaceSegementEvent,
            learningAssetType,
            currentUserTrack,
            isRefSheet = false,
            currentUserAnswers = {},
        } = this.props;
        let hideTableHeaders = false;
        if (currentUserTrack && currentUserTrack.track) {
            hideTableHeaders = currentUserTrack?.track?.hideQuestionAndResultsHeaders ?? false;
          }

        let showAnswerTable = false;
        Object.keys(currentUserAnswers).forEach((questionKey) => {
            if (
                currentUserAnswers[questionKey] &&
                !isEmpty(currentUserAnswers[questionKey]) &&
                !isUndefined(currentUserAnswers[questionKey]?.data) &&
                !isNull(currentUserAnswers[questionKey]?.data)
            ) {
                showAnswerTable = true;
            }
        });

        const isMobile = window.innerWidth <= 480;

        return (
            <div className={'showPreviousAnswersSlide'}>
                <WorkspaceSlideHeader
                    headerText={name}
                    currentSectionItemId={sectionItemId}
                    currentSectionId={sectionId}
                    currentUser={currentUser}
                    hideBookmark={hideBookmark}
                    isRefSheet={isRefSheet}
                />
                <div className={'playerContent'}>
                    <RichTextDisplay
                        className={'playerDescription'}
                        htmlToDisplay={description}
                        currentUser={currentUser}
                        nextSectionName={nextSectionName}
                        sendSegementEvent={sendWorkspaceSegementEvent}
                    />
                    {get(content, 'aggregateLikertResults.isActive', false) && this._renderLikertsAggregationSection()}
                    {(showAnswerTable || !isRefSheet) && (
                        isMobile ? (
                            <div className={'showPreviousAnswersSlide-mobile-view'}>
                                {this._getAnswersMobileView()}
                            </div>
                        ) : (
                            <Table striped={false} bordered={false} hover={false} className={'shortEssayItemAnswersList'}>
                                {!hideTableHeaders && (
                                    <thead>
                                        <tr>
                                            <th>
                                                <VerbText
                                                    messageId={'WORKSPACE_SHOW_PREV_QUESTION'}
                                                    defaultMessage={'Question'}
                                                />
                                            </th>
                                            <th>
                                                <VerbText
                                                    messageId={'WORKSPACE_SHOW_PREV_RESPONSE'}
                                                    defaultMessage={'Response'}
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                )}
                                <tbody>{this._generateAnswers()}</tbody>
                            </Table>
                        )
                    )}
                </div>
            </div>
        );
    }
}

export const ShowPreviousAnswersSlideComponent = ShowPreviousAnswersSlide;
export default TrackPlayerSlideComponent(ShowPreviousAnswersSlide);
